import { ContentApiDto, CustomerApiDto } from '@b2x/storefront-api-js-client/src';
import classnames from 'classnames';
import { FormikHelpers } from 'formik';
import React from 'react';
import * as yup from 'yup';

import { useCustomerApi } from '../api/useCustomerApi';
import { Button } from '../Button';
import { appConfig } from '../config';
import { ConsentsContentType } from '../contentTypes';
import { t } from '../i18n/i18n';
import { useContent } from '../useContent';
import { useInsideModalDetector } from '../useInsideModalDetector';
import { useModalCloser } from '../useModalCloser';
import { formatHtml, fromCustomerApiDtoToCustomerRequestApiDto, uiClassName } from '../util';
import { PropsWithCustomComponentWithoutChildren, VariantsController } from '../VariantsController';
import { Checkbox, CheckboxProps } from './fields/RadioCheck';
import {
  FormButtonProps,
  formikBoolean,
  getFormikBooleanValue,
  getInitialBoolean,
  isResetButtonDisabled,
  isSubmitButtonDisabled,
} from './Form';
import { FormGroup, FormGroupProps } from './FormGroup';
import { BaseHelpedFormProps, HelpedForm } from './HelpedForm';

export interface LoyaltyProgramFormProps extends BaseHelpedFormProps<
  FormValues,
  FieldsHelper,
  ValidationSchemaSelector,
  { content?: ContentApiDto<ConsentsContentType> }
> {
  customer: CustomerApiDto;
}

interface FormValues {
  loyaltyProgramConsent: formikBoolean;
}

type ValidationSchema = {
  loyaltyProgramConsent: yup.BooleanSchema;
};

interface ValidationSchemaSelector {}

interface FieldsHelper {
  buttons: {
    cancel?: FormButtonProps;
    reset: FormButtonProps;
    submit: FormButtonProps;
  };
  loyaltyProgramConsent: { checkbox: CheckboxProps; formGroup: FormGroupProps };
}

export const LoyaltyProgramFormHelper = ({
  children,
  className,
  customer,
  initialValues,
  onCancel,
  onSuccess,
  ...otherProps
}: LoyaltyProgramFormProps) => {
  const consentsContent = useContent<ConsentsContentType>('CONSENTS_CONTENT');

  const _initialValues = React.useMemo<FormValues>(
    () => ({
      loyaltyProgramConsent: getInitialBoolean(),
      ...initialValues,
    }),
    [initialValues],
  );

  const validationSchema = React.useMemo<ValidationSchema>(
    () => ({
      loyaltyProgramConsent: yup.boolean().required().oneOf([true], { key: 'custom.privacyRequired' }),
    }),
    [],
  );

  const { updateCustomer } = useCustomerApi();

  const handleSubmit = React.useCallback(
    (values: FormValues, formikHelpers: FormikHelpers<FormValues>) =>
      updateCustomer({
        ...fromCustomerApiDtoToCustomerRequestApiDto(customer),
        loyaltyProgramConsent: getFormikBooleanValue(values.loyaltyProgramConsent),
      }).then(() => {
        onSuccess && onSuccess();
      }),
    [customer, onSuccess, updateCustomer],
  );

  const ref = React.useRef<HTMLFormElement>(null);

  const { insideModal } = useInsideModalDetector();
  const closeModal = useModalCloser();

  return (
    <HelpedForm<FormValues>
      className={classnames('LoyaltyProgramForm', className)}
      enableReinitialize
      initialValues={_initialValues}
      innerRef={ref}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      {...otherProps}
    >
      {({ formik }) => {
        const fieldsHelper: FieldsHelper = {
          buttons: {
            cancel: onCancel
              ? {
                  label: t('form.loyaltyProgramForm.buttons.cancel.label'),
                  onClick: onCancel,
                  type: 'button',
                  variant: appConfig.form?.buttons.cancel?.defaultVariant,
                }
              : undefined,
            reset: {
              disabled: isResetButtonDisabled(formik),
              label: t('form.loyaltyProgramForm.buttons.reset.label'),
              type: 'reset',
              variant: appConfig.form?.buttons.cancel?.defaultVariant,
            },
            submit: {
              disabled: isSubmitButtonDisabled(formik),
              label: t('form.loyaltyProgramForm.buttons.submit.label'),
              type: 'submit',
              variant: appConfig.form?.buttons.submit?.defaultVariant,
            },
          },
          loyaltyProgramConsent: {
            checkbox: {
              id: 'loyaltyProgramConsent',
              label: formatHtml(consentsContent?.body.loyaltyProgram?.label),
              name: 'loyaltyProgramConsent',
            },
            formGroup: {
              names: ['loyaltyProgramConsent'],
            },
          },
        };
        return children ? (
          children({ closeModal, content: consentsContent, fieldsHelper, formik, insideModal })
        ) : (
          <>
            {consentsContent?.body.loyaltyProgram?.enabled && (
              <fieldset>
                <legend className={uiClassName({ bs5: 'visually-hidden' })}>
                  {fieldsHelper.loyaltyProgramConsent.checkbox.label}
                </legend>
                <FormGroup {...fieldsHelper.loyaltyProgramConsent.formGroup}>
                  <Checkbox {...fieldsHelper.loyaltyProgramConsent.checkbox} />
                </FormGroup>
              </fieldset>
            )}

            <Button {...fieldsHelper.buttons.submit} />
          </>
        );
      }}
    </HelpedForm>
  );
};

export type LoyaltyProgramFormVariants = '';

const LoyaltyProgramFormController = (props: PropsWithCustomComponentWithoutChildren<LoyaltyProgramFormProps>) => (
  <VariantsController<LoyaltyProgramFormProps, LoyaltyProgramFormVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: LoyaltyProgramFormHelper,
      name: 'LoyaltyProgramForm',
    }}
  />
);
export { LoyaltyProgramFormController as LoyaltyProgramForm };
